import {
  Box,
  Button,
  Card,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { PMPopUp } from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { Configuration } from "product-test-types";
import {
  getAssessmentConfig,
  getAuditConfig,
  getMaturityConfig,
  getPmoConfig,
  getRolloutConfig,
} from "product-test-utils";
import { useEffect, useState } from "react";

import { STRAPI_URL } from "../env";
import { usePimoForm } from "./pimo-form";

export type ReportingUseCasePageProps = {
  configuration?: Configuration;
  language?: string;
};

export const ReportingUseCasePage: PimoReactComponent<
  ReportingUseCasePageProps,
  "update:view-configuration",
  string
> = ({ language = "en", fireEvent, configuration }) => {
  const normalizedLanguage = language === "de" ? "de" : "en";
  const { setValue, formState } = usePimoForm<{
    configuration: Configuration;
    enableSaveButton?: boolean;
  }>({
    appStateSlice: "editSettingsForm",
  });
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [selectedUseCase, setSelectedUseCase] = useState(
    configuration?.useCase
  );

  useEffect(() => {
    // Map tab to config
    const configMap = {
      audit: getAuditConfig(normalizedLanguage),
      rollout: getRolloutConfig(normalizedLanguage),
      pmo: getPmoConfig(normalizedLanguage),
      assessment: getAssessmentConfig(normalizedLanguage),
      maturity: getMaturityConfig(normalizedLanguage),
    } as const;
    if (!selectedUseCase) return;
    if (selectedUseCase !== formState.configuration?.useCase) {
      setValue("enableSaveButton", true);
      setValue("configuration", configMap[selectedUseCase]);
      fireEvent?.("update:view-configuration", selectedUseCase);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedLanguage, selectedUseCase]);

  const createSampleProjects = async (): Promise<void> => {
    if (!selectedUseCase) return;

    try {
      const response = await fetch(
        `${STRAPI_URL}/project/sample-projects/${selectedUseCase}?${new URLSearchParams(
          { language }
        )}`,
        {
          credentials: "include",
          method: "GET",
        }
      );

      if (!response.ok) {
        throw new Error(
          `delete all projects failed with status ${response.status}.`
        );
      }

      setConfirmationDialogOpen(false);
    } catch {
      return;
    }
  };

  // Function to render content based on the selected tab
  const renderContent = () => {
    switch (selectedUseCase) {
      case "pmo":
        return (
          <Typography sx={{ fontSize: "14px" }}>
            {getLocalizedMessage(language, "reportingUseCase.pmoDescription") ||
              "In this use case, only Program Management can create findings and define associated measures (mitigations). Once created, the Finding Owner or their team is responsible for regularly reporting progress on each measure. This enables a clear separation of responsibilities while ensuring continuous tracking and resolution of identified risks."}
          </Typography>
        );
      case "rollout":
        return (
          <Typography sx={{ fontSize: "14px" }}>
            {getLocalizedMessage(
              language,
              "reportingUseCase.rolloutDescription"
            ) || "Rollout Status Reporting content goes here."}
          </Typography>
        );
      case "audit":
        return (
          <Typography sx={{ fontSize: "14px" }}>
            {getLocalizedMessage(
              language,
              "reportingUseCase.auditDescription"
            ) ||
              "In this use case, only Program Management can create findings and define associated measures (mitigations). Once created, the Finding Owner or their team is responsible for regularly reporting progress on each measure. This enables a clear separation of responsibilities while ensuring continuous tracking and resolution of identified risks."}
          </Typography>
        );
      case "assessment":
        return (
          <Typography sx={{ fontSize: "14px" }}>
            {getLocalizedMessage(
              language,
              "reportingUseCase.assessmentDescription"
            ) || "Assessment content goes here."}
          </Typography>
        );
      case "maturity":
        return (
          <Typography sx={{ fontSize: "14px" }}>
            {getLocalizedMessage(
              language,
              "reportingUseCase.maturityDescription"
            ) ||
              "In this use case, domains and their controls are tracked over time to assess and improve organisational maturity levels."}
          </Typography>
        );
      default:
        return null;
    }
  };

  return (
    <Card
      sx={{
        width: "100%",
        height: "calc(100vh - 84px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        p: 2,
        boxShadow: "none",
        gap: 2,
      }}
    >
      <Typography sx={{ fontSize: "24px", fontWeight: 500 }}>
        {getLocalizedMessage(language, "reportingUseCase.title") ||
          "Reporting Use Case"}
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
          color: grey[600],
        }}
      >
        {getLocalizedMessage(language, "reportingUseCase.description") ||
          "The Pimo reporting tool covers various use cases. The selection of the use case determines the structure of the reporting. Click on one of the use cases to find out more."}
      </Typography>
      <Typography
        sx={{
          fontSize: "16px",
          fontWeight: 500,
          textAlign: "left",
          width: "100%",
        }}
      >
        {getLocalizedMessage(language, "reportingUseCase.useCaseSelection") ||
          "Use Case Selection"}
      </Typography>
      <Paper
        sx={{
          p: 4,
          borderRadius: "12px",
          boxShadow: "none",
          border: "1px solid",
          borderColor: grey[300],
        }}
      >
        <Stack
          direction="row"
          mb={4}
          sx={{ display: "flex", justifyContent: "space-evenly" }}
        >
          <Button
            onClick={() => setSelectedUseCase("pmo")}
            sx={{
              px: 4,
              py: 2,
              borderRadius: "15px",
              textTransform: "none",
              fontSize: "14px",
              bgcolor: selectedUseCase === "pmo" ? "#2d3848" : undefined,
              color: selectedUseCase === "pmo" ? "#fff" : undefined,
              "&:hover": {
                bgcolor: selectedUseCase === "pmo" ? "#1e2a38" : undefined,
              },
              border: "1px solid",
              borderColor: grey[700],
            }}
          >
            {getLocalizedMessage(
              language,
              "reportingUseCase.projectStatusReporting"
            ) || "Project Status Reporting"}
          </Button>
          <Button
            onClick={() => setSelectedUseCase("rollout")}
            sx={{
              px: 4,
              py: 2,
              borderRadius: "12px",
              fontSize: "14px",
              textTransform: "none",
              bgcolor: selectedUseCase === "rollout" ? "#2d3848" : undefined,
              color: selectedUseCase === "rollout" ? "#fff" : undefined,
              "&:hover": {
                bgcolor: selectedUseCase === "rollout" ? "#1e2a38" : undefined,
              },
              border: "1px solid",
              borderColor: grey[700],
            }}
          >
            {getLocalizedMessage(
              language,
              "reportingUseCase.rolloutStatusReporting"
            ) || "Rollout Status Reporting"}
          </Button>
          <Button
            onClick={() => setSelectedUseCase("audit")}
            sx={{
              px: 4,
              py: 2,
              textTransform: "none",
              fontSize: "14px",
              borderRadius: "12px",
              bgcolor: selectedUseCase === "audit" ? "#2d3848" : undefined,
              color: selectedUseCase === "audit" ? "#fff" : undefined,
              "&:hover": {
                bgcolor: selectedUseCase === "audit" ? "#1e2a38" : undefined,
              },
              border: "1px solid",
              borderColor: grey[700],
            }}
          >
            {getLocalizedMessage(
              language,
              "reportingUseCase.measureReportingForFindings"
            ) || "Measure Reporting for Findings"}
          </Button>
          <Button
            onClick={() => setSelectedUseCase("assessment")}
            sx={{
              px: 4,
              py: 2,
              textTransform: "none",
              fontSize: "14px",
              borderRadius: "12px",
              bgcolor: selectedUseCase === "assessment" ? "#2d3848" : undefined,
              color: selectedUseCase === "assessment" ? "#fff" : undefined,
              "&:hover": {
                bgcolor:
                  selectedUseCase === "assessment" ? "#1e2a38" : undefined,
              },
              border: "1px solid",
              borderColor: grey[700],
            }}
          >
            Assessment
          </Button>
          <Button
            onClick={() => setSelectedUseCase("maturity")}
            sx={{
              px: 4,
              py: 2,
              textTransform: "none",
              fontSize: "14px",
              borderRadius: "12px",
              bgcolor: selectedUseCase === "maturity" ? "#2d3848" : undefined,
              color: selectedUseCase === "maturity" ? "#fff" : undefined,
              "&:hover": {
                bgcolor: selectedUseCase === "maturity" ? "#1e2a38" : undefined,
              },
              border: "1px solid",
              borderColor: grey[700],
            }}
          >
            Maturity
          </Button>
        </Stack>
        <Divider sx={{ mb: 4 }} />
        <Box>{renderContent()}</Box>
        <Divider sx={{ mb: 4 }} />
        <Box>
          <Typography>Add sample projects for selected use case:</Typography>
          <Button
            variant="outlined"
            disabled={formState.enableSaveButton}
            onClick={() => setConfirmationDialogOpen(true)}
          >
            {formState.enableSaveButton
              ? "Please save the settings first"
              : "Add Sample Projects"}
          </Button>
        </Box>
      </Paper>

      <PMPopUp
        open={confirmationDialogOpen}
        title="Confirm"
        description="Do you want to delete all projects? This action cannot be undone."
        onClose={() => setConfirmationDialogOpen(false)}
        maxWidth="sm"
        cancelButtonText={"Cancel"}
        cancelButtonFireEvent={() => setConfirmationDialogOpen(false)}
        cancelButtonColor="white"
        confirmationButtonColor="error"
        confirmationButtonText={
          getLocalizedMessage(language, "dialog.button.delete") || "Delete"
        }
        confirmationButtonFireEvent={() => createSampleProjects()}
      />
    </Card>
  );
};
